// =============================================================================
// VandieZ admin-web -- Wallet audit (/wallet/audit)
// =============================================================================
// Spec 8 Plan D. Read-only compliance trail for wallet money movement:
//   * Transfers tab -- driver-to-driver transfers (sender / recipient / amount).
//   * Recharges tab -- Razorpay recharge orders (user / amount / payment id).
//
// Mirrors SupportTicketsPage.tsx structure: filter buttons (here, two tabs)
// → paginated table (50/page) → pagination controls. Backed by walletAdminApi;
// the backend /admin/wallet/* endpoints land with Spec 8 Plan C.
// =============================================================================

import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { streamCsvDownload, walletAdminApi } from "@/api/client";
import { PageHeader } from "@/components/AdminLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { ApiError } from "@/types/auth";
import type { AdminRecharge, AdminWalletTransfer } from "@/types/wallet_audit";

const PAGE = 50;

type Tab = "transfers" | "recharges";

function formatDate(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

function shortId(id: string): string {
  return id.slice(0, 8);
}

function PartyCell({
  name,
  phone,
  id,
}: {
  name: string | null;
  phone: string | null;
  id: string;
}): React.JSX.Element {
  // Show the human name + phone first (operator's primary lookup), with
  // a tiny short-id underneath for traceability when an admin needs to
  // copy the row's user_id into another query.
  return (
    <div>
      <div className="font-medium">{name ?? "—"}</div>
      <div className="text-xs text-muted-foreground font-mono">
        {phone ?? shortId(id)}
      </div>
    </div>
  );
}

export function WalletAuditPage(): React.JSX.Element {
  const { accessToken, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState<Tab>("transfers");
  const [transfers, setTransfers] = useState<AdminWalletTransfer[]>([]);
  const [recharges, setRecharges] = useState<AdminRecharge[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [offset, setOffset] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);

  const fetchPage = useCallback(
    async (nextOffset: number, signal?: AbortSignal) => {
      if (accessToken === null) {
        return;
      }
      setLoading(true);
      try {
        if (tab === "transfers") {
          const res = await walletAdminApi.listTransfers(
            accessToken,
            { limit: PAGE, offset: nextOffset },
            signal,
          );
          setTransfers(res.items);
          setTotal(res.total);
        } else {
          const res = await walletAdminApi.listRecharges(
            accessToken,
            { limit: PAGE, offset: nextOffset },
            signal,
          );
          setRecharges(res.items);
          setTotal(res.total);
        }
      } catch (e: unknown) {
        if (e instanceof Error && e.name === "AbortError") {
          return;
        }
        if (e instanceof ApiError && e.problem.status === 401) {
          void logout();
          navigate("/login");
        }
      } finally {
        if (signal?.aborted !== true) {
          setLoading(false);
        }
      }
    },
    [accessToken, tab, logout, navigate],
  );

  useEffect(() => {
    const controller = new AbortController();
    setOffset(0);
    void fetchPage(0, controller.signal);
    return () => {
      controller.abort();
    };
  }, [tab, fetchPage]);

  const totalPages = Math.ceil(total / PAGE);
  const currentPage = Math.floor(offset / PAGE) + 1;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Wallet Audit"
        description="Recharges + driver-to-driver transfers (compliance trail)."
        actions={
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (accessToken === null) {return;}
              void streamCsvDownload(
                accessToken,
                "/admin/exports/ledger.csv",
                `vandiez-ledger-${new Date().toISOString().slice(0, 10)}.csv`,
              );
            }}
          >
            Export ledger CSV
          </Button>
        }
      />

      <div className="flex gap-2">
        {(["transfers", "recharges"] as Tab[]).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => {
              setTab(t);
            }}
            className={cn(
              "rounded-full px-4 py-1 text-sm font-medium",
              tab === t
                ? "bg-primary text-primary-foreground"
                : "bg-muted text-muted-foreground hover:bg-accent",
            )}
          >
            {t === "transfers" ? "Transfers" : "Recharges"}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{total} total</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : tab === "transfers" ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="pb-2 text-left">ID</th>
                  <th className="pb-2 text-left">Sender</th>
                  <th className="pb-2 text-left">Recipient</th>
                  <th className="pb-2 text-left">Amount</th>
                  <th className="pb-2 text-left">Status</th>
                  <th className="pb-2 text-left">Created</th>
                </tr>
              </thead>
              <tbody>
                {transfers.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 align-top">
                    <td className="py-2 font-mono text-xs">{shortId(r.id)}</td>
                    <td className="py-2">
                      <PartyCell
                        name={r.sender_name}
                        phone={r.sender_phone}
                        id={r.sender_id}
                      />
                    </td>
                    <td className="py-2">
                      <PartyCell
                        name={r.recipient_name}
                        phone={r.recipient_phone}
                        id={r.recipient_id}
                      />
                    </td>
                    <td className="py-2">₹{r.amount}</td>
                    <td className="py-2">{r.status}</td>
                    <td className="py-2 text-muted-foreground">
                      {formatDate(r.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b">
                  <th className="pb-2 text-left">ID</th>
                  <th className="pb-2 text-left">User</th>
                  <th className="pb-2 text-left">Amount</th>
                  <th className="pb-2 text-left">Status</th>
                  <th className="pb-2 text-left">Payment</th>
                  <th className="pb-2 text-left">Created</th>
                </tr>
              </thead>
              <tbody>
                {recharges.map((r) => (
                  <tr key={r.id} className="border-b last:border-0 align-top">
                    <td className="py-2 font-mono text-xs">{shortId(r.id)}</td>
                    <td className="py-2">
                      <PartyCell
                        name={r.user_name}
                        phone={r.user_phone}
                        id={r.user_id}
                      />
                    </td>
                    <td className="py-2">₹{r.amount}</td>
                    <td className="py-2">{r.status}</td>
                    <td className="py-2 font-mono text-xs">
                      {r.razorpay_payment_id ?? "—"}
                    </td>
                    <td className="py-2 text-muted-foreground">
                      {formatDate(r.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          {totalPages > 1 && (
            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset === 0}
                  onClick={() => {
                    const p = Math.max(0, offset - PAGE);
                    setOffset(p);
                    void fetchPage(p);
                  }}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={offset + PAGE >= total}
                  onClick={() => {
                    const n = offset + PAGE;
                    setOffset(n);
                    void fetchPage(n);
                  }}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
